import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import './CharacterEditor.css'
import { useCharacter } from "./CharacterProvider";
import CharacterSelector from "./CharacterSelector";
import CharacterForm from "./CharacterForm";
import CharacterSkills from "./CharacterSkills";
import CharacterAssignmentPopover from "./CharacterAssignmentPopover";
import { printCharacterSheet, previewCharacterSheet } from "./printCharacterSheet";
import * as api from "../../utils/api";
import useCharacterSkillCatalog from "./useCharacterSkillCatalog";
import CharacterDraftControls from './CharacterDraftControls';
import characterSaveSignature from './characterSaveSignature';


const CharacterEditor = ({token, playerId, staffPermissions}) => {
    const {currentCharacter, setCurrentCharacter} = useCharacter();
    const [races, setRaces] = useState([]);
    const [skillsets, setSkillsets] = useState([]);
    const skillCatalog = useCharacterSkillCatalog({ currentCharacter, setCurrentCharacter, token });
    const availableSkills = skillCatalog.skills;
    const [saving, setSaving] = useState(false);
    const [autosave, setAutosave] = useState(
        () => localStorage.getItem('autosave') !== 'false'
    );

    const prevCharacterIdRef = useRef(null);
    const autosaveTimerRef = useRef(null);
    const saveQueue = useRef(Promise.resolve());
    const pendingSaves = useRef(0);
    const characterSession = useRef(null);
    const [savedCharacter, setSavedCharacter] = useState(null);
    const [saveFailed, setSaveFailed] = useState(false);
    const [draftSavedAt, setDraftSavedAt] = useState(null);
    const snapshot = characterSaveSignature(currentCharacter, availableSkills);
    // Compare both versions with the same catalog, including when a delayed
    // catalog identifies transform abilities as automatically granted.
    const savedSnapshot = characterSaveSignature(savedCharacter, availableSkills);

    // A new visit to a character gets its own baseline. An older queued save
    // must not replace it, even if the user switches away and back to the same ID.
    useEffect(() => {
        if (characterSession.current?.id !== currentCharacter?.characterId) {
            characterSession.current = { id: currentCharacter?.characterId };
            setSavedCharacter(currentCharacter);
            setSaveFailed(false);
            setDraftSavedAt(null);
        }
    }, [currentCharacter]);

    useEffect(() => {
        document.title = currentCharacter?.name
            ? `${currentCharacter.name} | Chronicles Character Manager`
            : 'Character Manager';
        return () => { document.title = 'Chronicles'; };
    }, [currentCharacter?.name]);


    const saveCharacter = useCallback((characterData, { silent = false } = {}) => {
        const payload = { ...characterData, race: characterData.raceId };
        const session = characterSession.current;
        pendingSaves.current += 1;
        setSaving(true);
        // Keep canonical writes in request order. A slow earlier autosave must
        // settle before a newly loaded build can be saved to the same character.
        const queued = saveQueue.current.then(async () => {
            try {
                await api.updateCharacter(characterData.characterId, payload);
                if (characterSession.current === session) {
                    setSavedCharacter(characterData);
                    setSaveFailed(false);
                }
                if (!silent) toast.success("Save successful!");
            } catch (error) {
                if (characterSession.current === session) setSaveFailed(true);
                // Character 400 responses contain safe validation text. Never expose
                // unexpected/network errors (or server SQL details) in the toast.
                const validationMessage = error.response?.status === 400 && error.response?.data?.error;
                toast.error(typeof validationMessage === 'string' && validationMessage.trim()
                    ? validationMessage : "Failed to save character");
            } finally {
                pendingSaves.current -= 1;
                setTimeout(() => { if (!pendingSaves.current) setSaving(false); }, 500);
            }
        });
        saveQueue.current = queued;
        return queued;
    }, []);

    // Re-fetches the currently open character so its characterStatus and
    // status-scaled SP fields reflect a just-made Primary/Secondary change,
    // without dropping client-only fields (transformName/playerName) that
    // aren't part of the API response.
    const refreshCurrentCharacter = useCallback(async () => {
        if (!currentCharacter?.characterId) return;
        try {
            const refreshed = await api.fetchCharacter(currentCharacter.characterId);
            setCurrentCharacter((prev) => (prev ? { ...prev, ...refreshed } : prev));
        } catch {
            toast.error('Failed to refresh character after assignment change.');
        }
    }, [currentCharacter?.characterId, setCurrentCharacter]);

    // Autosave the canonical character after edits, while skipping the initial
    // load/switch and replacing any unsent save when a newer edit arrives.
    useEffect(() => {
        if (!currentCharacter?.characterId) return;
        if (prevCharacterIdRef.current !== currentCharacter.characterId) {
            prevCharacterIdRef.current = currentCharacter.characterId;
            return;
        }
        if (!autosave) return;
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = setTimeout(() => {
            saveCharacter(currentCharacter, { silent: true });
        }, 1000);
        return () => clearTimeout(autosaveTimerRef.current);
    }, [currentCharacter, autosave, saveCharacter]);

    useEffect(() => {
        (async function () {
            const results = await Promise.allSettled([api.fetchRaces(), api.fetchSkillsets()]);
            results.forEach((result, index) => {
                if (result.status === 'fulfilled') (index === 0 ? setRaces : setSkillsets)(result.value);
                else toast.error(index === 0 ? 'Failed to load races' : 'Failed to load skillsets');
            });
        })();
    }, []);

    const handlePrintCharacterSheet = useCallback(() => {
        if (!currentCharacter || !skillCatalog.ready) return;
        printCharacterSheet({
            character: currentCharacter,
            races,
            skillsets,
            availableSkills,
            printedByPlayerId: playerId,
        });
    }, [currentCharacter, races, skillsets, availableSkills, playerId, skillCatalog.ready]);

    // Ctrl+P (Cmd+P on Mac) triggers the same character sheet print as the
    // "Print" button, instead of the browser's own print
    // dialog for the Character Manager page itself.
    useEffect(() => {
        const onKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "p") {
                e.preventDefault();
                handlePrintCharacterSheet();
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [handlePrintCharacterSheet]);


    return (
        <div className="character-select-container character-editor">
            <div className="character-editor-heading">
                <h1 className="landing-title">Character Manager</h1>
            </div>
            <div className="character-editor-toolbar">
                <div className="character-selector-column">
                    <CharacterSelector token={token} playerId={playerId} staffPermissions={staffPermissions} />
                </div>
                {currentCharacter && (
                    <>
                        <div className="character-assignment">
                            <CharacterAssignmentPopover
                                compact
                                token={token}
                                playerId={currentCharacter.playerId}
                                currentCharacterId={currentCharacter.characterId}
                                currentCharacterStatus={currentCharacter.characterStatus}
                                staffPermissions={staffPermissions}
                                onAssignmentChanged={refreshCurrentCharacter}
                            />
                        </div>
                        <div className="character-editor-actions">
                            <div className="character-save-summary">
                                <span className={`character-save-status${saveFailed ? ' is-error' : snapshot !== savedSnapshot ? ' is-unsaved' : ''}`}
                                    aria-live="polite" aria-atomic="true">
                                    {saving ? 'Saving…' : saveFailed ? 'Save failed' : snapshot !== savedSnapshot ? 'Unsaved changes' : 'Saved'}
                                </span>
                                {draftSavedAt && <span className="character-draft-timestamp">Draft saved {draftSavedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>}
                            </div>
                            <div className="character-print-actions">
                                <button type="button" className="character-output-button" aria-label="Preview" title="Preview"
                                    onClick={() => previewCharacterSheet({ character: currentCharacter })} disabled={!skillCatalog.ready}>
                                    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                                        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" /><circle cx="12" cy="12" r="3" />
                                    </svg>
                                </button>
                                <button type="button" className="character-output-button" aria-label="Print" title="Print" onClick={handlePrintCharacterSheet} disabled={!skillCatalog.ready}>
                                    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round">
                                        <path d="M6 9V3h12v6M6 18H3V9h18v9h-3M6 15h12v7H6Z" /><path d="M17 12h1" />
                                    </svg>
                                </button>
                            </div>
                            <CharacterDraftControls key={currentCharacter.characterId} currentCharacter={currentCharacter}
                                setCurrentCharacter={setCurrentCharacter} catalog={skillCatalog} token={token}
                                autosave={autosave} onDraftSaved={setDraftSavedAt}
                                onToggleAutosave={() => setAutosave(value => {
                                    localStorage.setItem('autosave', String(!value));
                                    return !value;
                                })}
                                saveButton={<button type="button" className="character-save-button"
                                    onClick={() => saveCharacter(currentCharacter)} disabled={saving || autosave}>
                                    {saving ? 'Saving…' : 'Save Character'}
                                </button>} />
                        </div>
                    </>
                )}
            </div>

            {currentCharacter && (
                <>
                    {!skillCatalog.ready && (
                        <div role={skillCatalog.error ? 'alert' : 'status'}>
                            {skillCatalog.error
                                ? 'Available skills could not be loaded. Skill and skillset changes and printing are unavailable.'
                                : 'Loading available skills. Skill and skillset changes and printing will be available when loading finishes.'}
                            {skillCatalog.error && <button type="button" className="print-btn" onClick={skillCatalog.retry}>Retry loading skills</button>}
                        </div>
                    )}
                    <CharacterForm
                        races={races}
                        skillsets={skillsets}
                        availableSkills={availableSkills}
                        catalogReady={skillCatalog.ready}
                        staffPermissions={staffPermissions}
                    />
                    <CharacterSkills
                        skillsets={skillsets}
                        availableSkills={availableSkills}
                        catalogReady={skillCatalog.ready}
                    />
                </>
            )}

        </div>
    );
}

export default CharacterEditor;
